import React, { useState } from 'react'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { Button, Message, Icon } from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { to } from '../../utils/to'
import { handleGenericErrors, handleValidationErrors } from '../../utils/Products/errorHandler'
import { Product, ProductMergeStatus } from '../../hooks/useProduct'
import { PATH_PRODUCTS_EDIT, API_PRODUCT_MERGE } from '../../consts'

const notices: { [key: string]: string } = {
  duplicate: 'This product was reported as a duplicate of another product.',
  discontinued: 'This product was reported as discontinued.',
  'incorrect-brand': 'This product was reported as incorrectly branded.',
}

const RemovedMessage = ({ status }: { status: string }) => (
  <Message warning icon className="small-icon">
    <Icon name="trash alternate" />
    <Message.Content>
      {status === 'pending'
        ? 'This product is marked for removal and will be removed when it goes out of stock everywhere.'
        : 'This product is removed. This is irreversible.'}
    </Message.Content>
  </Message>
)

const NoticeMessage = ({ status }: { status: string }) => (
  <Message color="blue" icon className="small-icon">
    <Icon name="angle right" />
    <Message.Content>{notices[status]}</Message.Content>
  </Message>
)

const MergeMessage = ({
  status,
  removal_status,
  refetchProduct,
}: {
  status: ProductMergeStatus
  removal_status: string | null
  refetchProduct: () => void
}) => {
  const { duplicate_product, active_product, merged_time, failed_at, initiated_time } = status
  const COOLDOWN_MINUTES = 5

  const [merging, setMerging] = useState(false)

  const retryCooldown = () => {
    if (merging) return true
    if (removal_status === 'removed') return true

    // 5 min cooldown to prevent spamming.
    const COOLDOWN_INTERVAL = COOLDOWN_MINUTES * 60 * 1000

    const now = new Date()

    return now.valueOf() - Date.parse(initiated_time).valueOf() < COOLDOWN_INTERVAL
  }

  let message
  const active_product_link = (
    <Link to={`${PATH_PRODUCTS_EDIT}/${active_product.id}`}>{active_product.id}</Link>
  )
  const cooldown_message =
    retryCooldown() && removal_status !== 'removed'
      ? ` Retry will be available ${COOLDOWN_MINUTES} minutes after initiation.`
      : ''

  if (merged_time) {
    message = (
      <React.Fragment>
        Merge into {active_product_link} completed at {merged_time}.{cooldown_message}
      </React.Fragment>
    )
  } else if (failed_at) {
    message = (
      <React.Fragment>
        Merge into {active_product_link} failed at {failed_at}.{cooldown_message}
      </React.Fragment>
    )
  } else if (initiated_time) {
    message = (
      <React.Fragment>
        Merge into {active_product_link} initiated at {initiated_time}.{cooldown_message}
      </React.Fragment>
    )
  } else {
    message = (
      <React.Fragment>
        Merge into {active_product_link} not yet initiated.{cooldown_message}
      </React.Fragment>
    )
  }

  const handleRetryMerge = async (duplicate_product_uuid: string, active_product_uuid: string) => {
    const payload = {
      duplicate_product_id: duplicate_product_uuid,
      product_id: active_product_uuid,
    }

    const axiosRequestObj: AxiosRequestConfig = {
      url: `${API_PRODUCT_MERGE}`,
      method: 'PUT',
      data: payload,
      headers: { 'content-type': 'application/json' },
      withCredentials: true,
    }

    setMerging(true)

    const [error, response] = await to<AxiosResponse, AxiosError>(axios(axiosRequestObj))

    if (response) {
      toast('Product merge initiated', { containerId: 'default' })
      refetchProduct()
    } else if (error) {
      const validations = error.response?.data?.errors?.validations

      if (validations) {
        handleValidationErrors(validations)
      } else {
        handleGenericErrors(error)
      }
    }

    setMerging(false)
  }

  const allowRetry = () => {
    if (!merged_time) return true
    if (!removal_status) return true
    if (removal_status !== 'removed') return true

    return false
  }

  return (
    <Message warning icon className="small-icon">
      <Icon name="copy" />
      <Message.Content
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexFlow: 'row wrap',
        }}
      >
        <span>{message}</span>
        {allowRetry() && (
          <Button
            color="blue"
            onClick={() => handleRetryMerge(duplicate_product.uuid, active_product.uuid)}
            disabled={retryCooldown()}
          >
            Retry merge
          </Button>
        )}
      </Message.Content>
    </Message>
  )
}

export const ProductFlags = ({
  product,
  refetchProduct,
}: {
  product: Product | null
  refetchProduct: () => void
}) => {
  const { notice_status, removal_status, merge_status } = product || {}

  return (
    <>
      {notice_status && <NoticeMessage status={notice_status} />}
      {merge_status && (
        <MergeMessage
          status={merge_status}
          removal_status={removal_status || null}
          refetchProduct={refetchProduct}
        />
      )}
      {removal_status && <RemovedMessage status={removal_status} />}
    </>
  )
}
